import { Component, inject, OnInit } from '@angular/core'
import { FormGroup, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ThreadUtils } from '@danangelus/ngx-utils'
import { SessionService } from '../../../core/security/session.service'
import { ValidationUtils } from '../../../core/utils/validation.utils'
import { User } from '../../../models/user.model'
import { SsoService } from '../../../core/services/sso.service'
import { ChangePasswordForm } from '../../../models/form.model'
import { MessageModule } from 'primeng/message'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputGroupModule } from 'primeng/inputgroup'

const log = LoggerService.get('PasswordResetComponent')

/**
 * Handles resetting a users password
 *
 * @author Dan Bennett
 */
@Component({
    selector: 'app-password-reset',
    standalone: true,
    imports: [
        // Angular
        ReactiveFormsModule,
        // PrimeNG
        FloatLabelModule,
        MessageModule,
        InputTextModule,
        InputGroupAddonModule,
        InputGroupModule
    ],
    templateUrl: './password-reset.component.html',
    styleUrl: './password-reset.component.sass'
})
export class PasswordResetComponent implements OnInit {

    router = inject(Router)

    // @ts-expect-error Assigned in ngOnInit
    form: FormGroup<ChangePasswordForm>
    // @ts-expect-error Assigned in ngOnInit
    user: User
    messageType = ''

    sessionService = inject(SessionService)
    ssoService = inject(SsoService)

    ngOnInit(): void {
        this.user = this.sessionService.user()!
        this.form = new UntypedFormGroup({
            newPassword: new UntypedFormControl('', [Validators.required, Validators.pattern(ValidationUtils.PASSWORD_PATTERN)]),
            confirmPassword: new UntypedFormControl('', [Validators.required, Validators.pattern(ValidationUtils.PASSWORD_PATTERN)]),
        })
    }

    async save() {
        log.debug('save', 'Saving password new...')

        await this.validateForm()

        const newPass = this.form.controls['newPassword']!
        const confirmPass = this.form.controls['confirmPassword']!
        if (this.form.valid) {
            newPass.setErrors(null)
            confirmPass.setErrors(null)
            if (newPass.value !== confirmPass.value) {
                newPass.setErrors({ noMatch: true })
                confirmPass.setErrors({ noMatch: true })
                this.messageType = 'PASSWORD_NO_MATCH'
                return
            }
        }

        if (this.form.invalid) {
            if (newPass.getError('noMatch')) {
                this.messageType = 'PASSWORD_NO_MATCH'
            } else {
                this.messageType = 'FORM_INVALID'
            }
            return
        }

        try {
            const result = await this.ssoService.changePassword(newPass.value!, confirmPass.value!)
            if (result) {
                this.messageType = 'PASSWORD_CHANGED'
                this.ngOnInit()

                if (this.sessionService.user()?.passwordExpired) {
                    this.sessionService.passwordReset()
                    await ThreadUtils.sleep(500)
                    await this.router.navigate(['home'])
                }
            }
        } catch (e) {
            log.error(`Error while trying to reset the users password! ${e}`)
            this.messageType = 'INTERNAL_SERVER'
        }
    }

    async validateForm() {
        Object.values(this.form.controls).forEach((control: any) => {
            control.markAsDirty()
        })
        this.form.updateValueAndValidity()
    }
}
